import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Drawer, Box, Typography, Button, Chip, IconButton } from '@mui/material';
import ContactDialog from '../../components/shared/ContactDialog';
import CloseIcon from '@mui/icons-material/Close';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import { selectActivePromotion, selectPlanDefinitions } from '../../store/actions/licenseActions';

const PLAN_META = {
    basic: {
        Icon: BoltRoundedIcon,
        iconBg: 'rgba(217,119,6,0.1)',
        iconColor: '#D97706',
        price: '₪72/חודש',
        features: ['מסך ציבורי אחד', '2 מערכי תצוגה למסך', '2 לוחות למערך תצוגה', 'כל הרכיבים ללא הגבלה', 'תמיכה תוך 5 ימי עסקים'],
    },
    pro: {
        Icon: DiamondRoundedIcon,
        iconBg: 'rgba(30,58,95,0.1)',
        iconColor: '#1e3a5f',
        price: '₪101/חודש',
        features: ['עד 3 מסכים', '5 מערכי תצוגה למסך', '5 לוחות למערך תצוגה', 'כל הרכיבים ללא הגבלה', 'היסטוריית 5 גרסאות לכל לוח', 'ספריית רכיבים שמורים', 'תמיכה תוך 48 שעות'],
    },
    community: {
        Icon: GroupsRoundedIcon,
        iconBg: 'rgba(16,185,129,0.1)',
        iconColor: '#059669',
        price: 'בואו נדבר',
        features: ['מסכים ללא הגבלה', 'מערכי תצוגה ולוחות ללא הגבלה', 'גרסאות ורכיבים שמורים ללא הגבלה', 'הדרכה ואונבורדינג אישי', 'מנהל חשבון ייעודי', 'SLA מוגדר + תמיכה ישירה'],
    },
};

const UpgradeDrawer = () => {
    const dispatch = useDispatch();
    const { open, featureName, requiredPlan } = useSelector(state => state.app.upgradeDrawer || {});
    const activePromotion = useSelector(selectActivePromotion);
    const planDefinitions = useSelector(selectPlanDefinitions);

    const handleClose = () => dispatch({ type: 'CLOSE_UPGRADE_DRAWER' });
    const [contactOpen, setContactOpen] = useState(false);

    const planName = planDefinitions?.[requiredPlan]?.displayName || requiredPlan || 'Pro';
    const plansToShow = ['basic', 'pro', 'community'];

    return (
        <>
        <Drawer
            anchor="right"
            open={!!open}
            onClose={handleClose}
            sx={{ zIndex: 1400 }}
            PaperProps={{
                sx: {
                    width: 390,
                    bgcolor: '#fff',
                    direction: 'rtl',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
        >
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #112236 0%, #1e3a5f 60%, #2a4f7a 100%)',
                px: 3,
                py: 3,
                flexShrink: 0,
                position: 'relative',
            }}>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        color: 'rgba(255,255,255,0.5)',
                        '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>
                    בואו נמצא יחד את המסלול המתאים
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', lineHeight: 1.65 }}>
                    נשמח לענות על כל שאלה ולהמליץ על מה שמתאים לכם — נחזור אליכם בהקדם ממש
                </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>

                {/* Locked feature callout */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #fef9ec 0%, #fff8e1 100%)',
                    border: '1.5px solid #FCD34D',
                    borderRadius: '14px',
                    p: 2.5,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                }}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: 'rgba(217,119,6,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <LockOpenRoundedIcon sx={{ color: '#D97706', fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: '#92400E', fontSize: '0.9rem', mb: 0.4 }}>
                            {featureName || 'תכונה זו'} זמינה במסלולים מתקדמים
                        </Typography>
                        <Typography sx={{ color: '#78350F', fontSize: '0.78rem', lineHeight: 1.65 }}>
                            שדרוג יפתח גישה לתכונה זו ולכלים נוספים שיחסכו זמן ויגדילו את ההשפעה של השילוט שלכם
                        </Typography>
                    </Box>
                </Box>

                {/* Active Promotion Badge */}
                {activePromotion && (
                    <Box sx={{
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                        border: '1.5px solid #6EE7B7',
                        borderRadius: '12px',
                        p: 2,
                        mb: 3,
                        textAlign: 'center',
                    }}>
                        <Typography sx={{ fontWeight: 700, color: '#065F46', fontSize: '0.88rem' }}>
                            {activePromotion.badgeText || '🎉 מבצע מיוחד'}
                        </Typography>
                        {activePromotion.description && (
                            <Typography sx={{ color: '#047857', fontSize: '0.8rem', mt: 0.5 }}>
                                {activePromotion.description}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Plans */}
                <Typography sx={{ fontWeight: 700, color: '#1e3a5f', fontSize: '0.85rem', mb: 1.5, letterSpacing: '0.02em' }}>
                    המסלולים שלנו
                </Typography>

                {plansToShow.map(planKey => {
                    const meta = PLAN_META[planKey];
                    const displayName = planDefinitions?.[planKey]?.displayName || planKey;
                    const isRequired = planKey === requiredPlan;
                    const { Icon } = meta;

                    return (
                        <Box
                            key={planKey}
                            sx={{
                                border: `2px solid ${isRequired ? '#1e3a5f' : '#e2e8f0'}`,
                                borderRadius: '14px',
                                p: 2,
                                mb: 1.5,
                                bgcolor: isRequired ? 'rgba(30,58,95,0.03)' : '#fafafa',
                                position: 'relative',
                                transition: 'box-shadow 0.15s',
                                '&:hover': { boxShadow: '0 2px 14px rgba(30,58,95,0.1)' },
                            }}
                        >
                            {isRequired && (
                                <Chip
                                    label="מומלץ עבורך"
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: -11,
                                        right: 14,
                                        background: 'linear-gradient(135deg, #9a7209 0%, #c8960c 100%)',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '0.66rem',
                                        height: 22,
                                        boxShadow: '0 2px 6px rgba(154,114,9,0.35)',
                                        '& .MuiChip-label': { px: 1.25 }
                                    }}
                                />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                                <Box sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: '9px',
                                    bgcolor: meta.iconBg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Icon sx={{ color: meta.iconColor, fontSize: 19 }} />
                                </Box>
                                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontWeight: 700, color: '#1e3a5f', fontSize: '0.9rem' }}>
                                        {displayName}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 600, color: '#94a3b8', fontSize: '0.78rem' }}>
                                        {meta.price}
                                    </Typography>
                                </Box>
                            </Box>
                            {meta.features.map(f => (
                                <Typography key={f} sx={{
                                    color: '#64748b',
                                    fontSize: '0.76rem',
                                    lineHeight: 1.85,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                }}>
                                    <Box component="span" sx={{ color: meta.iconColor, fontWeight: 700 }}>✓</Box>
                                    {f}
                                </Typography>
                            ))}
                        </Box>
                    );
                })}
            </Box>

            {/* Footer */}
            <Box sx={{
                p: 2.5,
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flexShrink: 0,
                bgcolor: '#fafafa',
            }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setContactOpen(true)}
                    sx={{
                        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a9e 100%)',
                        color: '#fff',
                        fontWeight: 700,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        py: 1.25,
                        letterSpacing: '0.01em',
                        boxShadow: '0 4px 16px rgba(30,58,95,0.3)',
                        transition: 'box-shadow 0.2s, background 0.2s',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #112236 0%, #1e3a5f 100%)',
                            boxShadow: '0 6px 22px rgba(30,58,95,0.45)',
                        }
                    }}
                >
                    שלחו לנו הודעה — נחזור מהר
                </Button>
                <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.71rem', mt: 0.25 }}>
                    מגיבים תוך שעות ספורות · פתוחים לכל בקשה ורעיון
                </Typography>
                <Button
                    variant="text"
                    fullWidth
                    onClick={handleClose}
                    sx={{
                        color: '#94a3b8',
                        fontWeight: 400,
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        mt: -0.25,
                    }}
                >
                    אחזור אליכם מאוחר יותר
                </Button>
            </Box>
        </Drawer>

        <ContactDialog
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            prefill={{ subject: 'שאלה לגבי המסלולים', planName }}
        />
        </>
    );
};

export default UpgradeDrawer;
